import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Table, Td, Th } from "@/components/ui/Table";
import { useReference } from "@/hooks/useReference";
import { useToast } from "@/hooks/useToast";
import { exports as exportsApi, teams as teamsApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { strings } from "@/lib/strings";
import type { ExportFilters, ExportRecord, Team } from "@/lib/types";

/** The export dialog and history (docs/08 §8.9, docs/03 §3.6). */
export function ExportPage() {
  const { notify } = useToast();
  const { specimens, organisms } = useReference();

  const [filters, setFilters] = useState<ExportFilters>({
    include_drafts: false,
    include_void: false,
  });
  const [preview, setPreview] = useState<{ row_count: number; blocked_by_flags: number } | null>(
    null,
  );
  const [teams, setTeams] = useState<Team[]>([]);
  const [history, setHistory] = useState<ExportRecord[]>([]);
  const [generating, setGenerating] = useState(false);

  const refreshPreview = useCallback(async () => {
    try {
      setPreview(await exportsApi.preview(filters));
    } catch {
      setPreview(null);
    }
  }, [filters]);

  const loadHistory = useCallback(async () => {
    try {
      setHistory(await exportsApi.list());
    } catch {
      // The history is a convenience; failing to load it is not fatal.
    }
  }, []);

  useEffect(() => {
    void refreshPreview();
  }, [refreshPreview]);

  useEffect(() => {
    void loadHistory();
    void teamsApi.list().then(setTeams).catch(() => undefined);
  }, [loadHistory]);

  const set = <K extends keyof ExportFilters>(key: K, value: ExportFilters[K]) =>
    setFilters((current) => ({ ...current, [key]: value }));

  const generate = async () => {
    setGenerating(true);
    try {
      const record = await exportsApi.create(filters);
      if (record.status === "too_large") {
        notify(record.error ?? "Too many rows. Narrow the filters.", "error");
      } else {
        notify(`Generated ${record.row_count} rows.`, "success");
        await exportsApi.download(
          record.id,
          `eksuth-baclink-${record.created_at.slice(0, 10)}.csv`,
        );
      }
      await loadHistory();
    } catch {
      notify(strings.errors.generic, "error");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-slate-900">
        {strings.exportDialog.title}
      </h1>

      <Card>
        <CardHeader
          title="What to include"
          description="Only complete isolates with no unresolved blocking flag are exported."
        />
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field id="team" label="Team">
              <Select
                id="team"
                placeholder="All teams"
                value={filters.team ?? ""}
                onChange={(event) => set("team", event.target.value || null)}
                options={teams.map((team) => ({ value: team.slug, label: team.name }))}
              />
            </Field>
            <Field id="from" label="Specimen date from">
              <Input
                id="from"
                type="date"
                value={filters.date_from ?? ""}
                onChange={(event) => set("date_from", event.target.value || null)}
              />
            </Field>
            <Field id="to" label="Specimen date to">
              <Input
                id="to"
                type="date"
                value={filters.date_to ?? ""}
                onChange={(event) => set("date_to", event.target.value || null)}
              />
            </Field>
            <Field id="specimen" label="Specimen type">
              <Select
                id="specimen"
                placeholder="All specimens"
                value={filters.specimen_type ?? ""}
                onChange={(event) => set("specimen_type", event.target.value || null)}
                options={specimens.map((s) => ({ value: s.code, label: s.name }))}
              />
            </Field>
            <Field id="organism" label="Organism">
              <Select
                id="organism"
                placeholder="All organisms"
                value={filters.organism_code ?? ""}
                onChange={(event) => set("organism_code", event.target.value || null)}
                options={organisms.map((o) => ({ value: o.code, label: o.name }))}
              />
            </Field>
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={filters.include_drafts ?? false}
                onChange={(event) => set("include_drafts", event.target.checked)}
              />
              {strings.exportDialog.includeDrafts}
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={filters.include_void ?? false}
                onChange={(event) => set("include_void", event.target.checked)}
              />
              {strings.exportDialog.includeVoid}
            </label>
          </div>

          <div className="rounded-md bg-slate-50 p-3 text-sm">
            {preview ? (
              <>
                <p className="font-medium text-slate-900">
                  {strings.exportDialog.rowsIncluded(preview.row_count)}
                </p>
                {preview.blocked_by_flags > 0 && (
                  <p className="mt-1 text-warning">
                    {strings.exportDialog.blockedNotice(preview.blocked_by_flags)}
                  </p>
                )}
              </>
            ) : (
              <p className="text-slate-500">Counting…</p>
            )}
          </div>

          <Button
            loading={generating}
            disabled={!preview || preview.row_count === 0}
            onClick={() => void generate()}
          >
            {strings.exportDialog.generate}
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Past exports"
          description="Every export is recorded in the audit log with the Record IDs it contained."
        />
        {history.length === 0 ? (
          <EmptyState title="No exports yet." />
        ) : (
          <Table caption="Past exports">
            <thead>
              <tr>
                <Th>{strings.exportDialog.generatedAt}</Th>
                <Th className="text-right">Rows</Th>
                <Th>{strings.exportDialog.checksum}</Th>
                <Th>Status</Th>
                <Th>
                  <span className="sr-only">Download</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {history.map((record) => (
                <tr key={record.id}>
                  <Td className="whitespace-nowrap">{formatDateTime(record.created_at)}</Td>
                  <Td className="text-right tabular-nums">{record.row_count}</Td>
                  <Td
                    className="max-w-[12rem] truncate font-mono text-xs"
                    title={record.file_hash ?? ""}
                  >
                    {record.file_hash ?? "—"}
                  </Td>
                  <Td>
                    <Badge tone={record.status === "complete" ? "ok" : "warning"}>
                      {record.status}
                    </Badge>
                  </Td>
                  <Td className="whitespace-nowrap">
                    {record.status === "complete" && (
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            void exportsApi.download(
                              record.id,
                              `eksuth-baclink-${record.created_at.slice(0, 10)}.csv`,
                            )
                          }
                        >
                          CSV
                        </Button>
                        <a
                          href={exportsApi.metadataUrl(record.id)}
                          className="text-sm text-brand-700 underline"
                        >
                          Metadata
                        </a>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
